import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { StoryRecap } from "./story-recap";
import { RolePlay } from "./roleplay";
import AiChatPopup, { AiSparkleIcon } from "./components/AiChatPopup";
import { AiChatProvider } from "./AiChatContext";
import { apiFetch } from "../../../api";
import { trackEvent } from "../../../analytics/posthog";
import s from "./LearningBoard.module.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AUDIO_LESSONS = [
  { key: "roleplay", title: "Role Play", desc: "Hear English, speak Swedish, then hear the answer", endpoint: "roleplay-audio", suffix: "roleplay",
    icon: "M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z" },
];

const MODULES = [
  { id: "story_recap",         title: "Story",             step: 1, color: "indigo",   desc: "Replay the story sentence by sentence", difficulty: 1 },
  { id: "roleplay",            title: "Role Play",         step: 2, color: "purple",   desc: "Speak Swedish in real conversation scenarios", difficulty: 2 },
];

const MODULE_COMPONENTS = {
  story_recap: StoryRecap,
  roleplay: RolePlay,
};

/* ─── Difficulty stars ─── */
function DifficultyStars({ level, max = 4 }) {
  return (
    <div className={s.stars}>
      {Array.from({ length: max }, (_, i) => (
        <svg key={i} className={`${s.star} ${i < level ? s.starFilled : s.starEmpty}`} viewBox="0 0 20 20" fill="currentColor" width="11" height="11">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292Z" />
        </svg>
      ))}
    </div>
  );
}

/* ─── Header (shared by all states) ─── */
function BoardHeader({ onBack, story, onDownload, onAskAi }) {
  return (
    <div className={s.header}>
      {/* Top row: back button + action buttons */}
      <div className={s.headerTopRow}>
        <button className={s.backBtn} onClick={onBack} title="Back to library">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        {story ? (
          <>
            <button className={s.downloadAudioBtn} onClick={onDownload}>
              <svg className={s.downloadAudioIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12M12 16.5V3" />
              </svg>
              <span className={s.downloadAudioLabel}>Audio Lessons</span>
            </button>
            <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
              <AiSparkleIcon className={s.headerAiBtnIcon} />
              <span className={s.headerAiBtnLabel}>AI</span>
            </button>
          </>
        ) : null}
      </div>

      {/* Avatar + Centered topic */}
      <div className={s.headerTitleGroup}>
        {story?.has_avatar && (
          <div className={s.headerAvatarWrap}>
            <img
              className={s.headerAvatar}
              src={`${BACKEND}/api/v1/admin/story-avatar/${story.story_id}?t=${Date.now()}`}
              alt=""
            />
          </div>
        )}
        {story ? (
          <>
            <h2 className={s.headerTopicSv}>{story.topic_in_swedish ?? story.topic?.in_swedish}</h2>
            <p className={s.headerTopicEn}>{story.topic_in_english ?? story.topic?.in_english}</p>
          </>
        ) : (
          <h2 className={s.headerTitle}>Learning Board</h2>
        )}
      </div>

    </div>
  );
}

export default function LearningBoard({ storyId, onBack }) {
  const [boardData, setBoardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeModule, setActiveModule] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [moduleContext, setModuleContext] = useState(null);
  const [downloadStatuses, setDownloadStatuses] = useState({});
  const [playingKey, setPlayingKey] = useState(null);
  const [playProgress, setPlayProgress] = useState(0);
  const [lockedToast, setLockedToast] = useState(null);
  const playerRef = useRef(null);

  // Cleanup player on modal close or unmount
  useEffect(() => {
    if (!showDownloadModal) {
      if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
      setPlayingKey(null);
      setPlayProgress(0);
    }
  }, [showDownloadModal]);

  useEffect(() => {
    return () => { if (playerRef.current) playerRef.current.pause(); };
  }, []);

  const handlePlay = useCallback((key, endpoint) => {
    // Toggle off if already playing this key
    if (playingKey === key) {
      if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
      setPlayingKey(null);
      setPlayProgress(0);
      return;
    }
    // Stop any existing playback
    if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }

    setPlayingKey(key);
    setPlayProgress(0);

    const audio = new Audio(`${BACKEND}/api/v1/admin/${endpoint}/${storyId}`);
    playerRef.current = audio;

    // Use timeupdate for reliable progress tracking (works with redirects & streaming)
    audio.addEventListener("timeupdate", () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setPlayProgress(audio.currentTime / audio.duration);
      }
    });
    audio.addEventListener("ended", () => { setPlayingKey(null); setPlayProgress(0); playerRef.current = null; });
    audio.addEventListener("error", () => { setPlayingKey(null); setPlayProgress(0); playerRef.current = null; });
    audio.play().catch(() => { setPlayingKey(null); setPlayProgress(0); playerRef.current = null; });
  }, [playingKey, storyId]);

  const handleDownload = useCallback(async (key, endpoint, suffix) => {
    if (downloadStatuses[key] === "loading" || !storyId) return;
    setDownloadStatuses((prev) => ({ ...prev, [key]: "loading" }));
    try {
      const res = await apiFetch(`/api/v1/admin/${endpoint}/${storyId}`);
      if (!res.ok) throw new Error("Failed to download audio");
      const blob = await res.blob();

      const safeName = (boardData?.topic_in_swedish || "audio")
        .replace(/[^\w\s\u00C0-\u00FF]/gi, "")
        .replace(/\s+/g, "_");
      const filename = `${safeName}_${suffix}.mp3`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      setDownloadStatuses((prev) => ({ ...prev, [key]: "done" }));
      setTimeout(() => setDownloadStatuses((prev) => ({ ...prev, [key]: "idle" })), 3000);
    } catch {
      setDownloadStatuses((prev) => ({ ...prev, [key]: "idle" }));
    }
  }, [downloadStatuses, storyId, boardData]);

  const boardBaseContext = useMemo(() => {
    if (!boardData) return {};
    return { origin: "learning_board", story_topic: { in_swedish: boardData.topic_in_swedish, in_english: boardData.topic_in_english } };
  }, [boardData]);

  useEffect(() => {
    setModuleContext(null);
  }, [activeModule]);

  const aiChatValue = useMemo(() => ({ setModuleContext }), []);

  // Fetch board data (modules, topic, unique word count, audio availability)
  const fetchBoard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch(`/api/v1/dashboard/stories/${storyId}/modules`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to load learning board");
      }
      const data = await res.json();
      setBoardData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [storyId]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);



  // Loading state
  if (loading) {
    return (
      <div className={s.panel}>
        <BoardHeader onBack={onBack} />
        <div className={s.content}>
          <div className={s.emptyState}>
            <div className={s.spinner} />
            <p className={s.emptyDesc}>Loading story...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={s.panel}>
        <BoardHeader onBack={onBack} />
        <div className={s.content}>
          <div className={s.emptyState}>
            <h3 className={s.emptyTitle}>Something went wrong</h3>
            <p className={s.emptyDesc}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Derive values from boardData
  const moduleCompletions = boardData ? Object.fromEntries(boardData.modules.map(m => [m.module_id, m.has_user_completed])) : {};
  const audioAvailability = boardData?.audio_availability ?? null;

  // Resolve active module component
  const ModuleComponent = activeModule ? MODULE_COMPONENTS[activeModule] : null;

  const allDone = MODULES.every(m => !!moduleCompletions[m.id]);

  // ── Main render ──
  return (
    <AiChatProvider value={aiChatValue}>
      {activeModule && ModuleComponent ? (
          <ModuleComponent
            onBack={() => { setActiveModule(null); fetchBoard(); }}
            onAskAi={() => setShowAiChat(true)}
            storyId={storyId}
            moduleId={activeModule}
            onComplete={() => {
              trackEvent("learning_module_completed", { story_id: storyId, module_id: activeModule, module_title: MODULES.find(m => m.id === activeModule)?.title });
              setBoardData(prev => ({
                ...prev,
                modules: prev.modules.map(m =>
                  m.module_id === activeModule
                    ? { ...m, has_user_completed: true }
                    : m
                ),
              }));
            }}
          />
      ) : (
    <div className={s.panel}>
      <BoardHeader
        onBack={onBack}
        story={boardData}
        onDownload={() => setShowDownloadModal(true)}
        onAskAi={() => setShowAiChat(true)}
      />

      <div className={s.content}>
        {/* Quest Path — Serpentine journey */}
        <div className={s.questPath}>
          {MODULES.map((mod, i) => {
            const isCompleted = !!moduleCompletions[mod.id];
            const firstIncompleteIdx = MODULES.findIndex(m => !moduleCompletions[m.id]);
            const isActive = i === firstIncompleteIdx;
            const isLocked = i > firstIncompleteIdx && firstIncompleteIdx !== -1;
            const isLast = i === MODULES.length - 1;
            const side = i % 2 === 0 ? "left" : "right";

            return (
              <div key={mod.id} className={`${s.questStep} ${s[`step_${side}`]}`} style={{ animationDelay: `${0.08 + i * 0.07}s` }}>
                {/* Connector line */}
                {!isLast && <div className={`${s.connector} ${s[`connector_${side}`]} ${isLocked ? s.connectorLocked : ""} ${isCompleted ? s.connectorCompleted : ""}`} />}

                {/* Node */}
                <div
                  className={`${s.questNode} ${s[`node_${mod.color}`]} ${isLocked ? s.nodeLocked : ""} ${isCompleted ? s.nodeCompleted : ""} ${isActive ? s.nodeActive : ""}`}
                >
                  <div className={s.nodeRing}>
                    <div className={s.nodeInner}>
                      {isCompleted ? (
                        <svg className={s.tickIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      ) : isLocked ? (
                        <svg className={s.lockIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="5" y="11" width="14" height="10" rx="2" />
                          <path d="M8 11V7a4 4 0 1 1 8 0v4" />
                        </svg>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Info card */}
                <button
                  className={`${s.questInfo} ${isLocked ? s.questInfoLocked : ""} ${isCompleted ? s.questInfoCompleted : ""} ${isActive ? s.questInfoActive : ""}`}
                  onClick={() => {
                    if (isLocked) {
                      setLockedToast(mod.id);
                      setTimeout(() => setLockedToast(null), 2500);
                      return;
                    }
                    trackEvent("learning_module_started", { story_id: storyId, module_id: mod.id, module_title: mod.title });
                    setActiveModule(mod.id);
                  }}
                >
                  <div className={s.questInfoTop}>
                    <span className={s.stepBadge}>Step {mod.step}</span>
                    <DifficultyStars level={mod.difficulty} />
                  </div>
                  <h3 className={s.questTitle}>
                    {mod.title}
                    {mod.badge && (
                      <span className={`${s.modeBadge} ${s[`modeBadge_${mod.badgeColor}`] || ""}`}>{mod.badge}</span>
                    )}
                  </h3>
                  <p className={s.questDesc}>{mod.desc}</p>
                  {lockedToast === mod.id && (
                    <p className={s.lockedToast}>Finish the previous lesson to unlock</p>
                  )}
                </button>
              </div>
            );
          })}

          {/* Final trophy marker */}
          <div className={`${s.trophyMarker} ${allDone ? s.trophyDone : ""}`} style={{ animationDelay: `${0.08 + MODULES.length * 0.07}s` }}>
            <div className={`${s.trophyNode} ${allDone ? s.trophyNodeDone : ""}`}>
              {allDone ? (
                <svg viewBox="0 0 24 24" fill="none" width="30" height="30" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" width="28" height="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.504-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m0 0a2.625 2.625 0 0 0 5.006 0m-5.006 0H12m2.503 0H12m0-8.625v6" />
                </svg>
              )}
            </div>
            <span className={allDone ? s.trophyDoneLabel : s.trophyLabel}>
              {allDone ? "All Completed" : "Complete!"}
            </span>
          </div>
        </div>
      </div>

      {/* Download Audio Lesson Modal */}
      {showDownloadModal && (
        <div className={s.modalOverlay} onClick={() => setShowDownloadModal(false)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalAccent} />
            <div className={s.modalHeader}>
              <div className={s.modalHeaderLeft}>
                <div className={s.modalIconWrap}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2Zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2Z" />
                  </svg>
                </div>
                <div>
                  <h3 className={s.modalTitle}>Audio Lessons</h3>
                  <p className={s.modalSubtitle}>Listen offline or on the go</p>
                </div>
              </div>
              <button className={s.modalClose} onClick={() => setShowDownloadModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={s.modalBody}>
              {AUDIO_LESSONS.map((item, idx) => {
                const dlStatus = downloadStatuses[item.key] || "idle";
                const available = audioAvailability?.[item.key] ?? false;
                const isPlaying = playingKey === item.key;
                const isDownloading = dlStatus === "loading";
                const isDone = dlStatus === "done";

                return (
                  <div
                    key={item.key}
                    className={`${s.dlCard} ${isPlaying ? s.dlCardPlaying : ""} ${!available ? s.dlCardUnavailable : ""}`}
                    style={{ animationDelay: `${0.04 + idx * 0.04}s` }}
                  >
                    <div className={s.dlCardMain}>
                      {/* Icon */}
                      <div className={`${s.dlIcon} ${isPlaying ? s.dlIconPlaying : ""} ${!available ? s.dlIconUnavailable : ""}`}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d={item.icon} />
                        </svg>
                      </div>

                      {/* Text */}
                      <div className={s.dlText}>
                        <span className={s.dlTitle}>{item.title}</span>
                        <span className={s.dlDesc}>
                          {!available ? "Not generated yet" : isDone ? "Downloaded!" : isDownloading ? "Downloading..." : item.desc}
                        </span>
                      </div>

                      {/* Actions */}
                      {available && (
                        <div className={s.dlActions}>
                          <button
                            className={`${s.dlBtn} ${isPlaying ? s.dlBtnActive : ""}`}
                            onClick={() => handlePlay(item.key, item.endpoint)}
                            title={isPlaying ? "Stop" : "Play"}
                          >
                            {isPlaying ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="6" y="5" width="4" height="14" rx="1" />
                                <rect x="14" y="5" width="4" height="14" rx="1" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                              </svg>
                            )}
                          </button>
                          <button
                            className={`${s.dlBtn} ${isDone ? s.dlBtnDone : ""}`}
                            onClick={() => handleDownload(item.key, item.endpoint, item.suffix)}
                            disabled={isDownloading}
                            title="Download"
                          >
                            {isDownloading ? (
                              <div className={s.dlBtnSpinner} />
                            ) : isDone ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12M12 16.5V3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Progress bar — visible when playing */}
                    {isPlaying && (
                      <div className={s.dlProgress}>
                        <div className={s.dlProgressTrack}>
                          <div className={s.dlProgressFill} style={{ width: `${playProgress * 100}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
      )}

      {/* Universal AI FAB — only on the board, modules have their own AI button */}
      {!activeModule && (
        <button className={s.aiFab} onClick={() => setShowAiChat(true)} aria-label="AI tutor">
          <AiSparkleIcon className={s.aiFabIcon} />
          <span className={s.aiFabLabel}>AI</span>
        </button>
      )}

      {/* AI Chat Popup */}
      {showAiChat && (
        <AiChatPopup
          onClose={() => setShowAiChat(false)}
          context={moduleContext ?? boardBaseContext}
          storyId={storyId}
        />
      )}
    </AiChatProvider>
  );
}
